import React, { useEffect, useRef, useState } from "react";
import { Label, Select } from "flowbite-react";
import { Surface } from "../../../../atoms/Surface";
import { AlertMessage } from "../../../../atoms/AlertMessage";
import { HiInformationCircle, HiMail } from "react-icons/hi";
import {
  MdEdit,
  MdDraw,
  MdAttachFile,
  MdClose,
  MdLightbulb,
  MdScience,
  MdSchedule,
  MdVisibility,
} from "react-icons/md";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ACCEPTED_FILE_TYPES } from "../../../../../constants/fileTypes";
import { CustomInput } from "../../../../atoms/CustomInput";
import { CustomButton } from "../../../../atoms/CustomButton";
import HtmlEditor, { HtmlEditorRef } from "../../../../atoms/HtmlEditor";
import { RICH_TEXT_LIMITS } from "../../../../../utils/richText";
import useAuth from "../../../../../hooks/useAuth";
import { PlanTier, usePermissions } from "../../../../../utils/permissions";
import FeatureUpgradePrompt from "../../../../atoms/FeatureUpgradePrompt";
import { PlansModal } from "../../../../modals/PlansModal";
import NoTranslate from "../../../../atoms/NoTranslate";
import { Switch } from "../../../../atoms/Switch";

type Props = {
  control: any;
  watch: (name: string) => any;
  errors: any;
  setValue?: any;
  onCorrect?: () => void;
  isCorrectingMessage?: boolean;
  onPreview?: (htmlContent: string) => void;
  isRequestingPreview?: boolean;
  readOnly?: boolean;
};

export const AutomatedEmailSection: React.FC<Props> = ({
  control,
  watch,
  errors,
  setValue,
  onCorrect,
  isCorrectingMessage = false,
  onPreview,
  isRequestingPreview = false,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const permissions = usePermissions(user);
  const contentEditorRef = useRef<HtmlEditorRef | null>(null);
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalTargetPlan, setUpgradeModalTargetPlan] =
    useState<PlanTier>(PlanTier.PREMIUM);

  const isFree = permissions.isPlanTier(PlanTier.FREE);
  const isPremium = permissions.isPlanTier(PlanTier.PREMIUM);
  const hasAttachmentAccess = permissions.hasFeatureAccess(
    "CUSTOM_REGISTRATION_EMAIL_ATTACHMENTS"
  );

  const automatedEmailEnabled = watch("automatedEmail.enabled");
  const rawContent = watch("automatedEmail.content");
  const editorIsEmpty = contentEditorRef.current?.isEmpty() ?? true;
  const isContentUndefined = typeof rawContent === "undefined";
  const savedAttachments = watch("automatedEmail.attachments") || [];

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;

    const files = Array.from(event.target.files);
    const totalExisting = savedAttachments.length + attachmentFiles.length;
    const remainingSlots = 4 - totalExisting;

    if (remainingSlots <= 0) return;

    const newFiles = files.slice(0, remainingSlots);
    setAttachmentFiles((prev) => [...prev, ...newFiles]);

    event.target.value = "";
  };

  const handleRemoveNewFile = (index: number) => {
    setAttachmentFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveSavedAttachment = (index: number) => {
    const currentAttachments = watch("automatedEmail.attachments") || [];
    setValue(
      "automatedEmail.attachments",
      currentAttachments.filter((_: any, i: number) => i !== index)
    );
  };

  const handleViewAttachment = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleTriggerFileSelection = () => {
    attachmentInputRef.current?.click();
  };

  const handleRequestEmailPreview = () => {
    if (!onPreview) return;

    let htmlContent = "";
    if (contentEditorRef.current) {
      const editorHtml = contentEditorRef.current.getHtml();
      if (editorHtml && editorHtml.trim()) {
        htmlContent = editorHtml;
      }
    }

    if (!htmlContent) {
      const contentFromForm = watch("automatedEmail.content");
      if (contentFromForm) {
        htmlContent = contentFromForm;
      }
    }

    if (htmlContent) {
      onPreview(htmlContent);
    }
  };

  useEffect(() => {
    setValue("automatedEmail._files", attachmentFiles);
  }, [attachmentFiles, setValue]);

  const freeToPremiumBenefits = [
    {
      id: "benefit-1",
      text: t(
        "customRegistration.automatedEmail.upgrade.free.benefit1"
      ) as string,
    },
    {
      id: "benefit-2",
      text: t(
        "customRegistration.automatedEmail.upgrade.free.benefit2"
      ) as string,
    },
    {
      id: "benefit-3",
      text: t(
        "customRegistration.automatedEmail.upgrade.free.benefit3"
      ) as string,
    },
    {
      id: "benefit-4",
      text: t(
        "customRegistration.automatedEmail.upgrade.free.benefit4"
      ) as string,
    },
  ];

  const premiumToEnterpriseBenefits = [
    {
      id: "benefit-1",
      text: t(
        "customRegistration.automatedEmail.upgrade.premium.benefit1"
      ) as string,
    },
    {
      id: "benefit-2",
      text: t(
        "customRegistration.automatedEmail.upgrade.premium.benefit2"
      ) as string,
    },
  ];

  return (
    <Surface bodyClassName="p-4 sm:p-6">
      <div className="mb-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <HiMail size={20} className="text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-gray-900 text-base font-semibold mb-1">
              {t("customRegistration.automatedEmail.title")}
            </h3>
            <p className="text-gray-500 text-sm leading-tight">
              {t("customRegistration.automatedEmail.description")}
            </p>
          </div>
        </div>
      </div>

      {isFree ? (
        <FeatureUpgradePrompt
          title={
            t("customRegistration.automatedEmail.upgrade.free.title") as string
          }
          description={
            t(
              "customRegistration.automatedEmail.upgrade.free.description"
            ) as string
          }
          benefitObjects={freeToPremiumBenefits}
          icon={HiMail}
          iconTheme="blue"
          upgradeText={t("dashboard.upgrade.key3") as string}
          onUpgrade={() => {
            setUpgradeModalTargetPlan(PlanTier.PREMIUM);
            setShowUpgradeModal(true);
          }}
        />
      ) : (
        <>
          <div className="mb-4">
            <Controller
              name="automatedEmail.enabled"
              control={control}
              render={({ field }) => (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <Label
                    htmlFor="automated-email-toggle"
                    className="cursor-pointer"
                  >
                    <span className="text-sm font-medium text-gray-900">
                      {t("customRegistration.automatedEmail.enableLabel")}
                    </span>
                  </Label>
                  <Switch
                    id="automated-email-toggle"
                    checked={field.value || false}
                    onChange={(checked) => field.onChange(checked)}
                  />
                </div>
              )}
            />
          </div>
        </>
      )}

      {automatedEmailEnabled && (
        <div className="space-y-4 animate-slideIn">
          <AlertMessage color="info" icon={HiInformationCircle}>
            <span className="text-sm font-medium">
              {t("customRegistration.automatedEmail.appliesNote")}
            </span>
          </AlertMessage>

          <section className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-6">
              <h3 className="flex items-center text-lg font-semibold text-gray-900">
                <MdSchedule className="text-gray-400 flex-shrink-0" size={20} />
                <span className="ml-2">
                  {t("customRegistration.automatedEmail.sendTiming.title")}
                </span>
              </h3>
              <p className="mt-2 text-sm text-gray-600">
                {t("customRegistration.automatedEmail.sendTiming.description")}
              </p>
            </div>
            <NoTranslate>
              <Controller
                name="automatedEmail.sendDelay"
                control={control}
                render={({ field }) => (
                  <Select {...field} className="w-full">
                    <option value="immediate">
                      {t(
                        "customRegistration.automatedEmail.sendTiming.options.immediate"
                      )}
                    </option>
                    <option value="1hour">
                      {t(
                        "customRegistration.automatedEmail.sendTiming.options.1hour"
                      )}
                    </option>
                    <option value="6hours">
                      {t(
                        "customRegistration.automatedEmail.sendTiming.options.6hours"
                      )}
                    </option>
                    <option value="1day">
                      {t(
                        "customRegistration.automatedEmail.sendTiming.options.1day"
                      )}
                    </option>
                    <option value="2days">
                      {t(
                        "customRegistration.automatedEmail.sendTiming.options.2days"
                      )}
                    </option>
                  </Select>
                )}
              />
            </NoTranslate>
            {errors.automatedEmail?.sendDelay && (
              <p className="mt-2 text-sm font-medium text-red-600">
                {errors.automatedEmail.sendDelay.message}
              </p>
            )}
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-6">
              <h3 className="flex items-center text-lg font-semibold text-gray-900">
                <MdEdit className="text-gray-400 flex-shrink-0" size={20} />
                <span className="ml-2">
                  {t(
                    "dashboard.entities.viewEntityUser.sendMsg.sections.subjectLine"
                  )}
                </span>
              </h3>
            </div>
            <NoTranslate>
              <CustomInput
                name="automatedEmail.subject"
                control={control}
                error={errors.automatedEmail?.subject}
                helperText={errors?.automatedEmail?.subject?.message}
                placeholder={
                  t(
                    "dashboard.entities.viewEntityUser.sendMsg.inp1Placeholder"
                  ) as string
                }
                labelVariant="title"
                className="text-lg"
                maxLength={100}
              />
            </NoTranslate>
            <div className="mt-2 text-xs font-medium text-gray-500">
              <MdLightbulb className="inline text-gray-400" size={16} />
              <i>
                {" "}
                {t(
                  "customRegistration.automatedEmail.variablesHint"
                ).toString()}
              </i>
            </div>
          </section>

          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="flex items-center text-lg font-semibold text-gray-900">
                <MdDraw className="text-gray-400 flex-shrink-0" size={20} />
                <span className="ml-2">
                  {t(
                    "dashboard.entities.viewEntityUser.sendMsg.sections.messageContent"
                  )}
                </span>
              </h3>

              <div className="flex gap-2">
                {onCorrect && !readOnly && (
                  <CustomButton
                    variant="outline"
                    onClick={onCorrect}
                    disabled={
                      isCorrectingMessage ||
                      isContentUndefined ||
                      (contentEditorRef.current ? editorIsEmpty : false)
                    }
                    title={
                      isCorrectingMessage
                        ? (t(
                            "dashboard.entities.viewEntityUser.sendMsg.buttons.aiQualityProcessing"
                          ) as string)
                        : (t(
                            "dashboard.entities.viewEntityUser.sendMsg.buttons.aiQualityCheck"
                          ) as string)
                    }
                    isLoading={isCorrectingMessage}
                    comp={
                      <div className="flex items-center gap-2">
                        <MdScience className="flex-shrink-0" size={16} />
                        <span className="hidden sm:inline">
                          {isCorrectingMessage
                            ? t(
                                "dashboard.entities.viewEntityUser.sendMsg.buttons.aiQualityProcessing"
                              )
                            : t(
                                "dashboard.entities.viewEntityUser.sendMsg.buttons.aiQualityCheck"
                              )}
                        </span>
                      </div>
                    }
                  />
                )}

                <CustomButton
                  variant="outline"
                  onClick={handleRequestEmailPreview}
                  disabled={
                    isRequestingPreview ||
                    !watch("automatedEmail.subject") ||
                    !watch("automatedEmail.content")
                  }
                  title={
                    !watch("automatedEmail.subject") ||
                    !watch("automatedEmail.content")
                      ? (t(
                          "dashboard.entities.viewEntityUser.sendMsg.validation.completeRequiredFields"
                        ) as string)
                      : (t(
                          "dashboard.entities.viewEntityUser.sendMsg.buttons.previewEmail"
                        ) as string)
                  }
                  isLoading={isRequestingPreview}
                  comp={
                    <div className="flex items-center gap-2">
                      <MdVisibility className="flex-shrink-0" size={16} />
                      <span className="hidden sm:inline">
                        {t(
                          "dashboard.entities.viewEntityUser.sendMsg.buttons.previewEmail"
                        )}
                      </span>
                    </div>
                  }
                />
              </div>
            </div>

            <NoTranslate>
              <div className="rounded-lg border border-solid border-gray-300 bg-white">
                <Controller
                  name="automatedEmail.content"
                  control={control}
                  render={({ field }) => (
                    <HtmlEditor
                      ref={contentEditorRef}
                      value={field.value || ""}
                      onChange={(html) => field.onChange(html)}
                      placeholder={
                        t(
                          "dashboard.entities.viewEntityUser.sendMsg.inp2Placeholder"
                        ) as string
                      }
                      minHeight="300px"
                      maxHeight="500px"
                      className="border-gray-300"
                      maxLength={RICH_TEXT_LIMITS.welcomeEmail}
                      showCharacterCount={true}
                    />
                  )}
                />
              </div>
            </NoTranslate>

            {errors.automatedEmail?.content && (
              <p className="mt-3 text-sm font-medium text-red-600">
                {errors.automatedEmail.content.message}
              </p>
            )}
          </div>

          <section className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="flex flex-wrap items-center gap-2 text-lg font-semibold text-gray-900">
                    <span className="hidden sm:inline">
                      <MdAttachFile
                        className="text-gray-400 flex-shrink-0"
                        size={20}
                      />
                    </span>
                    <span>
                      {t(
                        "dashboard.entities.viewEntityUser.sendMsg.sections.attachments"
                      )}
                    </span>
                    <span className="text-sm font-normal text-gray-500 sm:text-base">
                      {t("dashboard.entities.viewEntityUser.sendMsg.optional")}
                    </span>
                  </h3>
                  <p className="mt-2 text-sm text-gray-600">
                    {t("customRegistration.automatedEmail.attachmentInfo")}
                  </p>
                </div>
              </div>
            </div>

            {isPremium && !hasAttachmentAccess ? (
              <FeatureUpgradePrompt
                title={
                  t(
                    "customRegistration.automatedEmail.upgrade.premium.title"
                  ) as string
                }
                description={
                  t(
                    "customRegistration.automatedEmail.upgrade.premium.description"
                  ) as string
                }
                benefitObjects={premiumToEnterpriseBenefits}
                icon={MdAttachFile}
                iconTheme="blue"
                upgradeText={t("dashboard.upgrade.key3") as string}
                onUpgrade={() => {
                  setUpgradeModalTargetPlan(PlanTier.ENTERPRISE);
                  setShowUpgradeModal(true);
                }}
              />
            ) : hasAttachmentAccess ? (
              <div className="grid gap-4">
                <input
                  ref={attachmentInputRef}
                  type="file"
                  multiple
                  accept={ACCEPTED_FILE_TYPES}
                  onChange={handleFileChange}
                  hidden
                />
                {(savedAttachments.length > 0 ||
                  attachmentFiles.length > 0) && (
                  <div className="mt-1 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-700 mb-3">
                      {t(
                        "customRegistration.automatedEmail.attachmentWarning.message"
                      )}
                    </p>
                    <div className="grid gap-2 text-sm">
                      <div className="flex items-start gap-2 text-green-700">
                        <span className="font-semibold flex-shrink-0">✓</span>
                        <span>
                          {t(
                            "customRegistration.automatedEmail.attachmentWarning.recommended"
                          )}
                        </span>
                      </div>
                      <div className="flex items-start gap-2 text-red-700">
                        <span className="font-semibold flex-shrink-0">✗</span>
                        <span>
                          {t(
                            "customRegistration.automatedEmail.attachmentWarning.avoid"
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                {savedAttachments.length > 0 && (
                  <div className="space-y-3 mb-4">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          {t(
                            "customRegistration.automatedEmail.savedAttachments",
                            {
                              count: savedAttachments.length,
                            }
                          )}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {t(
                            "customRegistration.automatedEmail.alreadyUploaded"
                          )}
                        </p>
                      </div>
                    </div>
                    <NoTranslate>
                      <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
                        {savedAttachments.map(
                          (attachment: any, index: number) => (
                            <div
                              key={`${attachment.name}-${index}`}
                              className="flex items-center justify-between p-4 transition-colors duration-150 hover:bg-gray-50"
                            >
                              <div className="flex min-w-0 flex-1 items-center">
                                <div className="mr-3 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100">
                                  <MdAttachFile
                                    className="text-gray-500"
                                    size={16}
                                  />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p
                                    className="hidden truncate text-sm font-medium text-gray-900 md:block"
                                    title={attachment.name}
                                  >
                                    {attachment.name.length > 35
                                      ? `...${attachment.name.slice(-35)}`
                                      : attachment.name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {(attachment.size / 1024).toFixed(1)} KB
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {attachment.url && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleViewAttachment(attachment.url)
                                    }
                                    className="group flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-blue-200 bg-blue-50 transition-colors duration-150 hover:border-blue-300 hover:bg-blue-100"
                                    title={
                                      t(
                                        "customRegistration.automatedEmail.viewFile"
                                      ) as string
                                    }
                                  >
                                    <MdVisibility
                                      className="text-blue-500 group-hover:text-blue-600"
                                      size={16}
                                    />
                                  </button>
                                )}
                                {!readOnly && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRemoveSavedAttachment(index)
                                    }
                                    className="group flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-red-200 bg-red-50 transition-colors duration-150 hover:border-red-300 hover:bg-red-100"
                                    title={
                                      t(
                                        "dashboard.entities.viewEntityUser.sendMsg.buttons.removeFile"
                                      ) as string
                                    }
                                  >
                                    <MdClose
                                      className="text-red-500 group-hover:text-red-600"
                                      size={16}
                                    />
                                  </button>
                                )}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </NoTranslate>
                  </div>
                )}
                {!readOnly &&
                  savedAttachments.length + attachmentFiles.length < 4 && (
                    <CustomButton
                      variant="outline"
                      onClick={handleTriggerFileSelection}
                      className="mb-4"
                      icon="paperClip"
                      title={
                        t(
                          "dashboard.entities.viewEntityUser.sendMsg.buttons.addFiles"
                        ) as string
                      }
                    />
                  )}
                {attachmentFiles.length > 0 && (
                  <div className="space-y-3">
                    <div className="mb-3">
                      <p className="text-sm font-semibold text-amber-900">
                        {t(
                          "dashboard.entities.viewEntityUser.sendMsg.attachedFiles",
                          {
                            count: attachmentFiles.length,
                          }
                        )}
                      </p>
                      <p className="text-xs text-amber-600 mt-0.5">
                        {t("customRegistration.automatedEmail.pendingUpload")} •{" "}
                        {t("common.saveToUpload")}
                      </p>
                    </div>
                    <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
                      {attachmentFiles.map((file, index) => (
                        <div
                          key={`${file.name}-${index}`}
                          className="flex items-center justify-between p-4 transition-colors duration-150 hover:bg-gray-50"
                        >
                          <div className="flex min-w-0 flex-1 items-center">
                            <div className="mr-3 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100">
                              <MdAttachFile
                                className="text-gray-500"
                                size={16}
                              />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p
                                className="hidden truncate text-sm font-medium text-gray-900 md:block"
                                title={file.name}
                              >
                                {file.name.length > 35
                                  ? `...${file.name.slice(-35)}`
                                  : file.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {(file.size / 1024).toFixed(1)} KB
                              </p>
                            </div>
                          </div>
                          {!readOnly && (
                            <button
                              type="button"
                              onClick={() => handleRemoveNewFile(index)}
                              className="group ml-3 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-red-200 bg-red-50 transition-colors duration-150 hover:border-red-300 hover:bg-red-100"
                              title={
                                t(
                                  "dashboard.entities.viewEntityUser.sendMsg.buttons.removeFile"
                                ) as string
                              }
                            >
                              <MdClose
                                className="text-red-500 group-hover:text-red-600"
                                size={16}
                              />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </section>
        </div>
      )}

      {errors.automatedEmail?.message && (
        <AlertMessage
          color="failure"
          icon={HiInformationCircle}
          className="mt-2"
        >
          {errors.automatedEmail.message}
        </AlertMessage>
      )}

      <PlansModal
        showPlansModal={showUpgradeModal}
        setShowPlansModal={setShowUpgradeModal}
        targetPlan={upgradeModalTargetPlan}
      />
    </Surface>
  );
};
